import { useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { PageHero, Section, Field, SerialSuccess } from "../components/PageKit";
import { usePageMeta } from "../lib/seo";
import { useT } from "../lib/i18n";
import { LanguageToggle } from "../components/LanguageToggle";

const TYPES = [
  ["unsafe_housing", "Unsafe housing"], ["no_water", "Community without water"], ["school", "School problem"], ["electricity", "Electricity problem"],
  ["food_insecurity", "Food insecurity"], ["emergency", "Emergency"], ["infrastructure", "Infrastructure issue"], ["vulnerable_household", "Vulnerable household"],
  ["opportunity", "Community opportunity"], ["other", "Other"],
];
const URGENCY = [["critical", "Critical — life or safety at risk"], ["high", "High"], ["moderate", "Moderate"], ["standard", "Standard"]];

export default function ReportNeed() {
  usePageMeta("Report a Community Need", "Anyone can report a community need to Dutsen Kauna Foundation — unsafe housing, water, schools, electricity, food insecurity or emergencies. You don't have to be the beneficiary.");
  const [f, setF] = useState({ need_type: "unsafe_housing", description: "", state: "", lga: "", community: "", landmark: "", people_affected: "", urgency: "standard", reporter_name: "", reporter_phone: "", reporter_email: "", reporter_relationship: "", consent_contact: false });
  const t = useT();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/community-needs", { ...f, people_affected: Number(f.people_affected) || 0 });
      setRes(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="report-need-page">
      <PageHero kicker="Listen first" title="Report a Community Need" text="Seen something the Foundation should know about? Tell us. You do not have to be the person affected — neighbours, teachers, health workers and leaders report needs every day." image="https://images.unsplash.com/photo-1565958923272-e96dbb1e8414?w=1800&q=80"><LanguageToggle /></PageHero>
      <Section className="bg-[#f8f4e8]">
        {res ? (
          <SerialSuccess serial={res.serial} title="Thank you — need received" text="Our research team will review the report and, where appropriate, begin community entry and validation. Keep this reference to follow up." testid="need-success" />
        ) : (
          <form onSubmit={submit} className="max-w-3xl mx-auto bg-white rounded-2xl p-8 border border-black/5 space-y-5" data-testid="need-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Type of need" value={f.need_type} onChange={set("need_type")} options={TYPES} testid="need-type" />
              <Field label="Urgency" value={f.urgency} onChange={set("urgency")} options={URGENCY} testid="need-urgency" />
            </div>
            <Field label="Describe the need" required textarea value={f.description} onChange={set("description")} testid="need-description" placeholder="What is happening, who is affected and for how long?" />
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="State" value={f.state} onChange={set("state")} testid="need-state" />
              <Field label="LGA" value={f.lga} onChange={set("lga")} testid="need-lga" />
              <Field label="Community / village" value={f.community} onChange={set("community")} testid="need-community" />
              <Field label="Nearest landmark" value={f.landmark} onChange={set("landmark")} testid="need-landmark" />
              <Field label="People affected (approx.)" type="number" value={f.people_affected} onChange={set("people_affected")} testid="need-people" />
              <Field label="Your relationship to the need" value={f.reporter_relationship} onChange={set("reporter_relationship")} testid="need-relationship" placeholder="e.g. neighbour, teacher, leader" />
            </div>
            <div className="p-5 rounded-xl bg-[#f8f4e8]">
              <div className="text-xs uppercase tracking-[0.18em] text-[#0d1724]/60 font-semibold mb-3">{t("About you (optional)")}</div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Name" value={f.reporter_name} onChange={set("reporter_name")} testid="need-reporter-name" />
                <Field label="Phone" value={f.reporter_phone} onChange={set("reporter_phone")} testid="need-reporter-phone" />
                <Field label="Email" type="email" value={f.reporter_email} onChange={set("reporter_email")} testid="need-reporter-email" />
              </div>
              <label className="flex items-center gap-2 text-sm mt-3"><input type="checkbox" checked={f.consent_contact} onChange={(e) => setF({ ...f, consent_contact: e.target.checked })} data-testid="need-consent" /> {t("The Foundation may contact me to verify this report.")}</label>
            </div>
            <button type="submit" disabled={busy} className="dk-btn-primary w-full justify-center disabled:opacity-60" data-testid="need-submit"><Send size={16} /> {busy ? t("Sending…") : t("Submit report")}</button>
          </form>
        )}
      </Section>
    </div>
  );
}
